/*
 * This code is the intellectual property of Bulb Inc. and other software publishers;
 * it may not be altered, copied or disclosed without prior written approval.
 * Copyright 2020, Bulb Inc. For more information, please email contact@hellobulb.com.
 *          _
 *  _            | | _
 * | |         _ | || |
 * | |__   _  | || || |__
 * | '_ \ | | | || || '_ \
 * | (_) || |_| || || (_) |
 *  \___/  \___/ |_| \___/
*/

import {
  Component,
  EventEmitter,
  forwardRef,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';

import { ControlValueAccessor, NG_VALUE_ACCESSOR, NgModel } from '@angular/forms';
import { TranslationService } from '../../common/translation.service';
import { generateObjectId } from '../../app/exports';

/**
 * Component ( Prototype ) for our aero-dropdown.
 */
@Component({
  selector: 'aero-multiselect',
  templateUrl: './aero-multiselect.component.html',
  styleUrls: ['./aero-multiselect.component.scss'],
  providers: [{
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => AeroMultiselectComponent),
    multi: true,
  }],
  standalone: false,
})
export class AeroMultiselectComponent implements ControlValueAccessor, OnChanges, OnInit {

  /**
   * Reference to the DOM input element.
   */
  @ViewChild('input') public aeroMultiselectNgModel: NgModel;

  @Input() id!: string;

  @Input() placeholder!: string;

  @Input() options: any[] = [];

  @Input() value: any[] = [];

  @Input() dataKey = 'id';

  @Input() optionLabel = 'name';

  @Input() optionValue = 'code';

  @Input() errorMessage: string;

  @Input() name: string;

  /**
   * Emit the current ngModel FormControl.
   */
  @Output() public aeroMultiselectNgModelEmitter: EventEmitter<any> = new EventEmitter<any>();

  @Input() label: string;

  @Input() required: boolean;

  @Output() valueChange = new EventEmitter<any[]>();

  /**
   * Default Constructor.
   */
  constructor(private $translate: TranslationService) {
  }

  /**
   * A lifecycle hook that is called after Angular has initialized all data-bound properties of a directive.
   * To handle any additional initialization tasks.
   */
  ngOnInit(): void {
    // Translate the default error message
    this.errorMessage = this.errorMessage || this.$translate.translateInstant('validation_error_required');
    // Each input field needs to be unique, or accessibility will explode!
    this.id = (this.id) ? this.id : generateObjectId();
  }

  /**
   * A lifecycle hook that is called when any data-bound property of a component changes.
   *
   * @param changes
   */
  ngOnChanges(changes: SimpleChanges) {
    if (changes.errors && this.aeroMultiselectNgModel) {
      // To set the errors of the input field.
      this.aeroMultiselectNgModel.control.setErrors(changes.errors.currentValue);
    }
  }

  /**
   * Get the value of the input.
   */
  get inputValue(): any {
    return this.value;
  }

  /**
   * Set the value of the input.
   *
   * @param newValue of the input field.
   */
  set inputValue(newValue: any) {
    if (this.value !== newValue) {
      this.value = newValue;
      this.onChange(newValue);
    }
  }

  /**
   * Callback function to register the UI value changes.
   */
  onChange: any = () => {
  };

  /**
   * Callback function to register on element touch.
   */
  onTouched: any = () => {
  };

  /**
   * Set the touched on the blur event.
   */
  onBlur() {
    this.onTouched();
  }

  /**
   * Method to set the value.
   *
   * @param updateValue of the input field.
   */
  writeValue(updateValue: any) {
    if (updateValue !== this.value) {
      this.value = updateValue;
    }
  }

  /**
   * Method gets triggered when the UI element value changes.
   *
   * @param updateValue of the input field.
   */
  registerOnChange(updateValue: any) {
    this.onChange = updateValue;
  }

  /**
   * Method gets triggered when the UI element value changes.
   *
   * @param updateValue of the input field.
   */
  registerOnTouched(updateValue: any) {
    this.onTouched = updateValue;
  }

  /**
   * Method that is invoked on an update of a model.
   */
  updateChanges() {
    this.aeroMultiselectNgModelEmitter.emit(this.aeroMultiselectNgModel);
    this.onChange(this.value);
  }

  /**
   * Method called by the parent component to reset this field.
   */
  resetField() {
    const fieldControl = this.aeroMultiselectNgModel.control;
    fieldControl.setErrors(null);
    fieldControl.markAsPristine();
    fieldControl.markAsUntouched();
  }
}
